import Button from "../../../ui/Button"
import { Disclosure, Transition } from "@headlessui/react"
import classNames from "../../../../utils/classNames"
import { ChevronRightIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid"
import SelectInput, { Option } from "../../../ui/SelectInput"
import { SideNavTabOption } from "~/components/Admin/Settings/PricesForm/types"
import formatCampingStyleName from "~/utils/formatCampingStyleName"
import { useCampingStyles } from "~/api2/hooks/useCampingStyles"
import { useNavigate, useLocation } from "react-router-dom"
import useSearchParams from "~/hooks/useSearchParams"
import { tabOptions } from "./constants"
import { useContext, useEffect, useMemo, useState } from "react"
import { AdminContext } from "~/contexts/AdminContext"
import eAdminUserRole from "~/enums/eAdminUserRole"
import { ROUTER_PATHS } from "~/routes/constants"
import { useMe } from "~/api2/hooks/useMe"
import { useProducts } from "~/api2/hooks/useProducts"
import InventoryWarningIcon from "../../../ui/InventoryWarningIcon"
import { useDynamicFormsSchema } from "~/hooks/useDynamicFormsSchema"
import slugify from "~/utils/admin/slugify"

interface SettingsNavBarProps {
  onTabChange: (tab: string) => void
}

const SettingsNavBar = ({ onTabChange }: SettingsNavBarProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const searchParams = useSearchParams()
  const selectedSubTab = searchParams.get("s")
  const { index: { data: dynamicFormSchema } } = useDynamicFormsSchema()
  const [dynamicTabOptions, setDynamicTabOptions] = useState<SideNavTabOption[]>([])

  const { data: user } = useMe()

  const filteredTabOptions = useMemo(() => {
    if (!user?.roles?.includes(eAdminUserRole.ParkAdmin)) {
      return tabOptions.filter((t) => ![ROUTER_PATHS.ADMIN_SETTINGS_RESERVATION_IMPORT, ROUTER_PATHS.ADMIN_SETTINGS_SITES, ROUTER_PATHS.ADMIN_SETTINGS_CAMPER_IMPORT].includes(t.value))
    }
    return [...tabOptions, ...dynamicTabOptions]
  }, [user, dynamicTabOptions])

  // Add section header index calculation for dynamic tabs
  const dynamicStartIndex = useMemo(() => {
    return filteredTabOptions.findIndex((tabOption) =>
      dynamicTabOptions.some((d) => d.value === tabOption.value)
    )
  }, [filteredTabOptions, dynamicTabOptions])

  useEffect(() => {
    if (!dynamicFormSchema?.models) return

    setDynamicTabOptions((prev) =>
      dynamicFormSchema.models.map(({ model }) => ({
        label: model,
        value: `${ROUTER_PATHS.ADMIN_SETTINGS}/${slugify(model)}`
      }))
    )
  }, [dynamicFormSchema])

  const {
    index: { data: campingStyles },
  } = useCampingStyles()

  const { data: productsData } = useProducts()
  const products = productsData?.add_ons || []

  const setSelectedOption = (option: Option) => {
    onTabChange(option.value)
    navigate(option.value)
  }

  return (
    <div className="xs:flex md:block py-4 xs:pl-4 lg:px-0">
      <div className="flex justify-between w-full">
        <h2 className="font-bold text-2xl mb-2.5">Settings</h2>
        <div className="sm:block md:hidden ml-3 w-56">
          <SelectInput
            options={filteredTabOptions}
            selected={
              filteredTabOptions.find((option: Option) => option.value === pathname) || { label: "", value: "" }
            }
            setSelected={setSelectedOption}
            large
          />
        </div>
      </div>
      <div className="w-48 mt-12 hidden md:block">
        {filteredTabOptions.map((tab: SideNavTabOption, index: number) => (
          <div key={tab.value}>
            {index === dynamicStartIndex && dynamicTabOptions.length > 0 && (
              <h3 className="px-2 pt-8 pb-4 text-sm text-gray-400 font-bold uppercase">Park Software Admin</h3>
            )}
            {tab.subOptions && campingStyles ? (
              <Disclosure key={`disclosure-${pathname}`} defaultOpen={selectedSubTab?.startsWith("pricing")}>
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      className="w-full text-left py-2.5 px-2 flex items-center justify-between"
                      onClick={() => setSelectedOption(tab)}
                    >
                      {formatCampingStyleName(tab.label)}
                      <span
                        className={`ml-2 transform transition-transform ease-in-out duration-300 ${open ? "rotate-90" : "rotate-0"
                          }`}
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </span>
                    </Disclosure.Button>
                    <Transition
                      show={open}
                      enter="transition-opacity duration-300"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition-opacity duration-300"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Disclosure.Panel static>
                        <div className="ml-2 mt-2 space-y-2">
                          {[
                            ...(tab.subOptions || []),
                            // TODO: disabled the camp pricing until its fixed
                            // ...((isParkAdminUser &&
                            //   campingStyles?.map(({ id, name }) => ({
                            //     label: name,
                            //     value: id,
                            //   }))) ||
                            //   []),
                          ].map((tabOption) => {
                            return (
                              <Button
                                key={tabOption.value}
                                onClick={() => {
                                  localStorage.setItem("from", window.location.pathname)
                                }}
                                variant="link"
                                className={classNames(
                                  "w-full text-left px-2 py-2.5",
                                  selectedSubTab === `pricing_${tabOption.value}` &&
                                  "bg-green-100 text-green-700 font-semibold hover:no-underline cursor-default",
                                )}
                              >
                                {formatCampingStyleName(tabOption.label)}
                              </Button>
                            )
                          })}
                        </div>
                      </Disclosure.Panel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            ) : (
              <Button
                variant="link"
                className={classNames(
                  "w-full text-left px-2 py-2.5",
                  pathname === tab.value &&
                  "bg-green-100 text-green-700 font-semibold hover:no-underline cursor-default",
                )}
                size="no-size"
                onClick={() => setSelectedOption(tab)}
              >
                <span className="flex items-center gap-1">
                  {tab.label}
                  {tab.label === "Products" && <InventoryWarningIcon products={products} size="md" />}
                </span>
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default SettingsNavBar
